import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  supplierRefFromId,
  useSupplierArticle,
  SupplierArticleBody,
} from '../components/SupplierArticleView';
import { BackButton } from '../components/ui/BackButton';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import i18n from '../i18n';
import { formatEUR } from '../lib/format';

interface ProductProj {
  [k: string]: unknown;
}
interface Availability {
  productId: string;
  internalStock: number;
  /** Shelf locations, main first (feedback 3.8). */
  locations?: string[];
  price: {
    unitPriceHT: number;
    finalUnitPriceHT: number;
    unitPriceTTC: number;
    discountPct: number;
  } | null; // null when the product has a non-positive margin and can't be priced
  supplierAvailable: boolean;
  supplierLeadDays: number | null;
  // Live per-supplier detail. Empty for roles without SUPPLIER_COMPARE(_VIEW); `unitPriceHT` is
  // a purchase cost and only present for margin authority.
  supplierAvailability: {
    supplierCode: string;
    supplierName: string;
    supplierArticleNo: string | null;
    status: 'OK' | 'UNAVAILABLE' | 'CIRCUIT_OPEN' | 'TIMEOUT' | 'MANUAL_ENTRY' | 'API_ERROR';
    availableQty: number | null;
    leadDays: number | null;
    cutoffTime: string | null;
    cutoffPassed: boolean;
    manualEntry: boolean;
    bestPrice: boolean;
    unitPriceHT?: number | null;
  }[];
}

/** Distinguish "no stock" from "we couldn't ask" — a timed-out supplier is not an out-of-stock one. */
function supplierStatusLabel(
  status: Availability['supplierAvailability'][number]['status']
): string {
  return i18n.t(`catalog.supplierStatus.${status}`, { defaultValue: status });
}

/** Screen 10 — Product Detail (projected fields per role). */
export default function ProductDetail() {
  const { id = '' } = useParams();
  // A supplier row has no product record, and its id is the synthetic `sup:CODE:ref` key the search
  // hands out. The orders page links here for "More information", so this route must render the
  // supplier sheet rather than ask the products endpoint for a UUID it will never parse.
  const supplierRef = supplierRefFromId(id);
  if (supplierRef) return <SupplierArticlePage reference={supplierRef} />;
  return <CatalogProductDetail id={id} />;
}

/** The supplier sheet as a full page, so a part we don't carry still has a linkable URL. */
function SupplierArticlePage({ reference }: { reference: string }) {
  const { t } = useTranslation();
  const { detail: d, failed } = useSupplierArticle(reference);
  return (
    <div className="space-y-4">
      <BackButton to="/catalog" label={t('catalog.backToCatalogue')} className="mb-0" />
      <h1 className="border-b pb-4 text-xl font-semibold tracking-tight">
        {d?.designation ?? reference}
        <span className="ml-2 rounded bg-warning/15 px-1.5 py-0.5 align-middle text-[11px] font-medium text-warning">
          {t('catalog.notInCatalogue')}
        </span>
      </h1>
      <SupplierArticleBody detail={d} failed={failed} reference={reference} />
    </div>
  );
}

function CatalogProductDetail({ id }: { id: string }) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [product, setProduct] = useState<ProductProj | null>(null);
  const [avail, setAvail] = useState<Availability | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await apiClient.get<ProductProj>(`/catalog/products/${id}`);
        const a = await apiClient.get<Availability>(`/catalog/products/${id}/availability`);
        if (!alive) return;
        setProduct(p.data);
        setAvail(a.data);
      } catch (err) {
        showApiError(err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, showApiError]);

  if (!product) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4">
      <BackButton to="/catalog" label={t('catalog.backToCatalogue')} className="mb-0" />
      <h1 className="border-b pb-4 text-xl font-semibold tracking-tight">
        {String(product.name ?? product.sku)}
      </h1>
      <section className="border rounded p-4 bg-card">
        <h2 className="font-semibold mb-2">{t('catalog.identifiers')}</h2>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          {Object.entries(product).map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="font-mono text-xs break-all">{String(v ?? '—')}</dd>
            </div>
          ))}
        </dl>
      </section>

      {avail && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('catalog.availability')}</h2>
          <div className="text-sm space-y-1">
            <div>
              {t('catalog.internalStock')} <strong>{avail.internalStock}</strong>
            </div>
            {/* Feedback 3.8 — where the part is, when it has a shelf. */}
            {(avail.locations ?? []).length > 0 && (
              <div>
                {t('catalog.locations')}{' '}
                <strong className="font-mono">{(avail.locations ?? []).join(' · ')}</strong>
              </div>
            )}
            {avail.price ? (
              <>
                <div>
                  {t('catalog.saleHT')} <strong>{formatEUR(avail.price.finalUnitPriceHT)}</strong>
                </div>
                <div>
                  {t('catalog.saleTTC')} <strong>{formatEUR(avail.price.unitPriceTTC)}</strong>
                </div>
                {avail.price.discountPct > 0 && (
                  <div className="text-success">
                    {t('catalog.gridDiscount', { pct: avail.price.discountPct })}
                  </div>
                )}
              </>
            ) : (
              <div className="text-warning">{t('catalog.priceUnavailable')}</div>
            )}
          </div>
          {avail.supplierAvailability.length > 0 && (
            <>
              <h3 className="font-semibold mt-3 mb-1 text-sm">
                {t('catalog.supplierAvailability')}
              </h3>
              <ul className="text-xs space-y-1">
                {avail.supplierAvailability.map((s) => (
                  <li key={s.supplierCode} className={s.bestPrice ? 'text-success' : undefined}>
                    <span className="font-mono">{s.supplierCode}</span> — {s.supplierName}
                    {s.bestPrice && <span className="ml-1">{t('catalog.best')}</span>}
                    <span className="ml-2">{supplierStatusLabel(s.status)}</span>
                    {s.availableQty != null && <span className="ml-1">({s.availableQty})</span>}
                    {s.leadDays != null && (
                      <span className="ml-2">{t('catalog.lead', { days: s.leadDays })}</span>
                    )}
                    {s.cutoffTime && (
                      <span className={`ml-2 ${s.cutoffPassed ? 'text-warning' : ''}`}>
                        {t('catalog.cutoff', { time: s.cutoffTime })}
                        {s.cutoffPassed && t('catalog.cutoffPassed')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}
